import React, { useState } from 'react'
import { useRouter } from 'next/router'
import Fuse from 'fuse.js'
import { FormInput, Tab, WContainer } from '@next-nx/shared-ui/atoms'
import { FaqInterface } from '@next-nx/utils/types'
import { faqMacroCategories } from 'libs/shared-ui/utils/const'
import { NavigationCancel } from '@next-nx/shared-ui/icons'
import { useTrans, useTealium } from '@next-nx/hooks'
import { getTimestamp } from '@next-nx/utils'
import styles from './FaqSearchAndMacroCategories.module.sass'
import Link from 'next/link'

interface Option {
  text: string
  url: string
}

interface Props {
  selectedMacroCategory: string
  macroCategoryOnClick: (e: string) => void
  faqs: FaqInterface[]
  className?: string
}

const FaqSearchAndMacroCategories = (props: Props) => {
  const { selectedMacroCategory, macroCategoryOnClick, faqs, className = '' } = props

  const fuse = new Fuse(faqs, {
    includeMatches: true,
    minMatchCharLength: 2,
    keys: ['domanda', 'risposta'],
  })
  const t = useTrans()
  const router = useRouter()
  const { linkDataLayer } = useTealium()

  const [inputValue, setInputValue] = useState<string>('')
  const [currentOptions, setCurrentOptions] = useState<Option[]>([])

  const handleTracking = (
    action_type: string = '',
    faq_category: string = '',
    faq_title: string = '',
    searchTerm?: string
  ) => {
    const action_timestamp = getTimestamp()
    if (!!faq_category.length === false) {
      if (!!faq_title.length === false) {
        let arrPath = router.asPath.split('?')
        arrPath = arrPath[0].split('/')
        faq_category = arrPath[arrPath.length - 1]
      } else {
        let faq: string | string[] = faq_title.split('?')[0]
        faq = faq.split('/')
        faq_category = faq[faq.length - 1]
      }
    }

    faq_title = searchTerm ? searchTerm : faq_title.split('faq=')[1]

    linkDataLayer({
      virtual: 'action',
      action_name: 'faq',
      action_tech: 'click',
      action_type: action_type.replace(/\.|\/|\s+/g, '_').toLowerCase(),
      action_timestamp,
      action_effect: 'faq_ricerca',
      faq_category: faq_category.replace(/\-|\s+/g, '_').toLowerCase(),
      faq_title: faq_title.replace(/\-|\s+/g, '_').toLowerCase(),
    })
  }

  const handleOnSubmit = (e: any) => {
    if (!inputValue?.length) {
      e.preventDefault()
    }
    !!inputValue?.length && handleTracking('diretta', '', '', inputValue) // deve partire quando inputValue esiste
  }

  const handleLinkClick = (e: any) => {
    setInputValue('')
    setCurrentOptions([])
    e.preventDefault()
    router.push({
      pathname: e.target.title.split('?')[0],
      query: { faq: e.target.title.split('?')[1].split('=')[1] },
    })
  }

  const onChangeInput = (e: any) => {
    const value = e.target.value
    setInputValue(value)

    let options = fuse.search(value).map((item) => {
      let domanda = item.item.domanda
      if ('matches' in item && item.matches) {
        for (let i = 0; i < item.matches?.length; i++) {
          if (item.matches[i].key === 'domanda') {
            const indices = item.matches[i].indices
            for (let y = indices.length - 1; y >= 0; y--) {
              const sub = domanda?.substring(indices[y][0], indices[y][1] + 1)
              if (sub)
                domanda =
                  domanda?.slice(0, indices[y][0]) +
                  `<span class="${styles.text_matches}">${sub}</span>` +
                  domanda?.slice(indices[y][1] + 1, domanda?.length)
            }
          }
        }
      }
      return { text: domanda ? domanda : '', url: item.item.url }
    })
    options.length = Math.min(options.length, 4)

    setCurrentOptions(options)
  }

  return (
    <div className={`${styles.root} ${className}`}>
      <WContainer variant="xs">
        <form className={styles.form} action="#" onSubmit={handleOnSubmit}>
          <div className={`${styles.inputWrap}`}>
            <FormInput
              name="q"
              value={inputValue}
              onChange={onChangeInput}
              placeholder={t('Cerca un argomento')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                }
              }}
            />
            {!!inputValue && (
              <button type="button" className={styles.inputReset} onClick={() => setInputValue('')}>
                <NavigationCancel />
              </button>
            )}
          </div>
        </form>
        {!!inputValue && (
          <div className={styles.search_container}>
            <div className={styles.search_container_intern}>
              {currentOptions.map((item) => (
                <Link href={item.url} key={item.url}>
                  <div
                    className={styles.search_option}
                    title={item.url}
                    onClick={(e) => {
                      handleTracking('suggerimento', '', item.url)
                      handleLinkClick(e)
                    }}
                  >
                    <span
                      className={styles.search_option_item}
                      title={item.url}
                      dangerouslySetInnerHTML={{ __html: item.text }}
                    ></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </WContainer>
      <WContainer>
        <div className={styles.macro_categories_buttons}>
          {faqMacroCategories.map((item) => (
            <Tab.Trigger
              label={item.text}
              active={selectedMacroCategory == item.key}
              onClick={() => macroCategoryOnClick(item.key)}
              className={styles.macro_categories_buttons_item}
              key={item.key}
            />
          ))}
        </div>
      </WContainer>
    </div>
  )
}

export default FaqSearchAndMacroCategories
